import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 로컬 스토리지에 토큰 저장
const setToken = token => {
    localStorage.setItem('user_token', token);
};

// 로컬 스토리지에서 토큰 가져오기
const getToken = () => {
    return localStorage.getItem('user_token');
};

// 로컬 스토리지에서 토큰 삭제
const removeToken = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('session_id');
};

// 리프레시 토큰 저장
const setRefreshToken = token => {
    localStorage.setItem('refresh_token', token);
};

// 리프레시 토큰 가져오기
const getRefreshToken = () => {
    return localStorage.getItem('refresh_token');
};

// 세션 ID 저장
const setSessionId = sessionId => {
    localStorage.setItem('session_id', sessionId);
};

// 세션 ID 가져오기
const getSessionId = () => {
    return localStorage.getItem('session_id');
};

// 사용자 정보 저장
const setUserInfo = user => {
    localStorage.setItem('user_info', JSON.stringify(user));
};

const getCurrentUserId = () => {
    try {
        const userInfo = localStorage.getItem('user_info');
        if (!userInfo) return null;

        const parsed = JSON.parse(userInfo);
        return parsed.id || parsed.user_id;
    } catch (e) {
        console.error('user_info 파싱 오류:', e);
        return null;
    }
};

// 사용자 정보 가져오기
const getUserInfo = () => {
    const userInfo = localStorage.getItem('user_info');
    return userInfo ? JSON.parse(userInfo) : null;
};

// 사용자 등급 가져오기
const getUserGrade = () => {
    const userInfo = getUserInfo();
    return userInfo ? userInfo.grade : null;
};

// 사용 기간 만료 확인
const isSubscriptionExpired = () => {
    const userInfo = getUserInfo();
    if (!userInfo || !userInfo.subscriptionEndDate) return true;

    const endDate = new Date(userInfo.subscriptionEndDate);
    const now = new Date();

    return now > endDate;
};

// 사용 기간 남은 일수 계산
const getRemainingDays = () => {
    const userInfo = getUserInfo();
    if (!userInfo || !userInfo.subscriptionEndDate) return 0;

    const endDate = new Date(userInfo.subscriptionEndDate);
    const now = new Date();

    // 이미 만료된 경우
    if (now > endDate) return 0;

    // 남은 일수 계산 (밀리초 → 일)
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
};

// 세션 유효성 검증
const validateSession = async () => {
    try {
        const sessionId = getSessionId();
        if (!sessionId) return false;

        const response = await authAxios.get(`${API_URL}/auth/validate-session`);
        return response.data.valid;
    } catch (error) {
        console.error('세션 검증 오류:', error);
        return false;
    }
};

// 중복 로그인 이벤트 리스너 설정
const setupSessionMonitoring = () => {
    // 웹소켓 또는 polling으로 세션 만료 이벤트 모니터링
    const sessionCheckInterval = setInterval(async () => {
        const isValid = await validateSession();
        if (!isValid && getToken()) {
            // 다른 곳에서 로그인 발생 - 현재 세션 로그아웃
            alert('다른 기기에서 로그인이 감지되었습니다. 현재 세션을 종료합니다.');
            logout();
            window.location.href = '/login';
            clearInterval(sessionCheckInterval);
        }
    }, 30000); // 30초마다 체크

    // 창이 닫힐 때 인터벌 정리
    window.addEventListener('beforeunload', () => {
        clearInterval(sessionCheckInterval);
    });

    return sessionCheckInterval;
};

// 로그인 요청
const login = async (userId, password) => {
    try {
        // 디바이스/브라우저 정보 수집
        const deviceInfo = {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screenSize: `${window.screen.width}x${window.screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timestamp: new Date().toISOString(),
        };

        const response = await axios.post(`${API_URL}/auth/login`, {
            userId,
            password,
            deviceInfo, // 디바이스 정보 포함
        });

        if (response.data.token) {
            // 사용 기간 만료 확인
            if (response.data.user && response.data.user.subscriptionEndDate) {
                const endDate = new Date(response.data.user.subscriptionEndDate);
                const now = new Date();

                if (now > endDate) {
                    throw {
                        message: '이용 기간이 만료되었습니다. 관리자에게 문의하세요.',
                        subscriptionExpired: true,
                    };
                }
            }

            setToken(response.data.token);
            setRefreshToken(response.data.refreshToken);
            setUserInfo(response.data.user);

            // 세션 ID 저장
            if (response.data.sessionId) {
                setSessionId(response.data.sessionId);
                setupSessionMonitoring();
            }

            return response.data;
        }
        return null;
    } catch (error) {
        throw error.response?.data || error || { message: '로그인 중 오류가 발생했습니다.' };
    }
};

// 리프레시 토큰으로 새 액세스 토큰 요청
const refreshAccessToken = async () => {
    try {
        const refreshToken = getRefreshToken();
        const sessionId = getSessionId();

        if (!refreshToken) {
            throw new Error('리프레시 토큰이 없습니다');
        }

        const response = await axios.post(`${API_URL}/auth/refresh-token`, {
            refreshToken,
            sessionId, // 세션 ID 포함
        });

        if (response.data.token) {
            // 사용 기간 만료 확인
            if (response.data.user && response.data.user.subscriptionEndDate) {
                const endDate = new Date(response.data.user.subscriptionEndDate);
                const now = new Date();

                if (now > endDate) {
                    throw {
                        message: '이용 기간이 만료되었습니다. 관리자에게 문의하세요.',
                        subscriptionExpired: true,
                    };
                }
            }

            setToken(response.data.token);
            // 세션 ID가 변경된 경우 업데이트
            if (response.data.sessionId) {
                setSessionId(response.data.sessionId);
            }
            // 사용자 정보가 포함되어 있다면 업데이트
            if (response.data.user) {
                setUserInfo(response.data.user);
            }
            return response.data.token;
        }
        return null;
    } catch (error) {
        console.error('토큰 갱신 오류:', error);
        // 리프레시 토큰도 만료된 경우 로그아웃 처리
        logout();
        window.location.href = '/login';
        throw error;
    }
};

// 로그아웃
const logout = async () => {
    try {
        const refreshToken = getRefreshToken();
        const sessionId = getSessionId();

        if (refreshToken && sessionId) {
            // 서버에 로그아웃 요청 (리프레시 토큰 무효화)
            await axios.post(`${API_URL}/auth/logout`, {
                refreshToken,
                sessionId,
            });
        }
    } catch (error) {
        console.error('로그아웃 오류:', error);
    } finally {
        // 로컬 스토리지에서 토큰 및 사용자 정보 삭제
        removeToken();
        localStorage.removeItem('user_info');

        // 항상 로그인 페이지로 리다이렉트
        window.location.href = '/login';
    }
};

// 등급별 권한 체크
const checkGradePermission = requiredGrade => {
    const userInfo = getUserInfo();
    if (!userInfo) return false;

    // 등급 우선순위 정의 (높은 숫자가 더 높은 권한)
    const gradePriority = {
        user: 1,
        premium: 2,
        manager: 3,
        admin: 4,
    };

    const userGrade = userInfo.grade || 'user';
    const userPriority = gradePriority[userGrade] || 0;
    const requiredPriority = gradePriority[requiredGrade] || 0;

    return userPriority >= requiredPriority;
};

// 사용자 등급 업데이트 (관리자용)
const updateUserGrade = async (userId, newGrade) => {
    try {
        const response = await authAxios.put(`${API_URL}/admin/users/${userId}/grade`, {
            grade: newGrade,
        });
        return response.data;
    } catch (error) {
        console.error('등급 업데이트 오류:', error);
        throw error.response?.data || { message: '등급 업데이트 중 오류가 발생했습니다.' };
    }
};

// 사용 기간 업데이트 (관리자용)
const updateUserSubscription = async (userId, daysToAdd, reason = '') => {
    try {
        const response = await authAxios.put(`${API_URL}/admin/users/${userId}/subscription`, {
            daysToAdd,
            reason,
        });
        return response.data;
    } catch (error) {
        console.error('사용 기간 업데이트 오류:', error);
        throw error.response?.data || { message: '사용 기간 업데이트 중 오류가 발생했습니다.' };
    }
};

// 모든 사용자 목록 가져오기 (관리자용)
const getAllUsers = async () => {
    try {
        const response = await authAxios.get(`${API_URL}/admin/users`);
        return response.data;
    } catch (error) {
        console.error('사용자 목록 로드 오류:', error);
        throw error.response?.data || { message: '사용자 목록을 불러오는 중 오류가 발생했습니다.' };
    }
};

// 로그인 기록 조회 (관리자용)
const getLoginLogs = async (filters = {}) => {
    try {
        const response = await authAxios.get(`${API_URL}/admin/login-logs`, { params: filters });
        return response.data;
    } catch (error) {
        console.error('로그인 기록 로드 오류:', error);
        throw error.response?.data || { message: '로그인 기록을 불러오는 중 오류가 발생했습니다.' };
    }
};

// 현재 활성 세션 목록 조회 (관리자용)
const getActiveSessions = async () => {
    try {
        const response = await authAxios.get(`${API_URL}/admin/active-sessions`);
        return response.data;
    } catch (error) {
        console.error('활성 세션 로드 오류:', error);
        throw error.response?.data || { message: '활성 세션을 불러오는 중 오류가 발생했습니다.' };
    }
};

// 특정 세션 강제 종료 (관리자용)
const terminateSession = async sessionId => {
    try {
        const response = await authAxios.delete(`${API_URL}/admin/sessions/${sessionId}`);
        return response.data;
    } catch (error) {
        console.error('세션 종료 오류:', error);
        throw error.response?.data || { message: '세션 종료 중 오류가 발생했습니다.' };
    }
};

// 사용 기간 이력 조회 (관리자용)
const getSubscriptionHistory = async userId => {
    try {
        const response = await authAxios.get(`${API_URL}/admin/subscription-history`, {
            params: { userId },
        });
        return response.data;
    } catch (error) {
        console.error('사용 기간 이력 로드 오류:', error);
        throw (
            error.response?.data || { message: '사용 기간 이력을 불러오는 중 오류가 발생했습니다.' }
        );
    }
};

// 인증이 필요한 API 요청에 사용할 Axios 인스턴스
const authAxios = axios.create({
    baseURL: API_URL,
});

// 요청 인터셉터 - 토큰을 헤더에 추가
authAxios.interceptors.request.use(
    config => {
        const token = getToken();
        const sessionId = getSessionId();

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        // 세션 ID가 있으면 헤더에 추가
        if (sessionId) {
            config.headers['X-Session-ID'] = sessionId;
        }

        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// 응답 인터셉터 - 토큰 만료 시 리프레시 토큰으로 새 토큰 요청
authAxios.interceptors.response.use(
    response => {
        return response;
    },
    async error => {
        const originalRequest = error.config;

        // 토큰이 만료되었고, 재시도하지 않은 요청인 경우
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // 리프레시 토큰으로 새 액세스 토큰 요청
                const newToken = await refreshAccessToken();
                if (newToken) {
                    // 새 토큰으로 헤더 업데이트
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    // 세션 ID 업데이트
                    const sessionId = getSessionId();
                    if (sessionId) {
                        originalRequest.headers['X-Session-ID'] = sessionId;
                    }
                    // 원래 요청 재시도
                    return authAxios(originalRequest);
                }
            } catch (refreshError) {
                // 리프레시 토큰도 만료된 경우
                console.error('토큰 갱신 실패:', refreshError);
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }
        // 중복 로그인 감지 시 (409 Conflict)
        else if (error.response && error.response.status === 409) {
            // 사용자에게 알림
            alert('다른 기기에서 로그인이 감지되었습니다. 현재 세션을 종료합니다.');
            // 로그아웃 처리
            logout();
            // 로그인 페이지로 이동
            window.location.href = '/login';
        }
        // 사용 기간 만료 시 (402 Payment Required)
        else if (error.response && error.response.status === 402) {
            // 사용자에게 알림
            alert('이용 기간이 만료되었습니다. 관리자에게 문의하세요.');
            // 로그아웃 처리
            logout();
            // 로그인 페이지로 이동
            window.location.href = '/login';
        }

        return Promise.reject(error);
    }
);

const AuthService = {
    login,
    logout,
    getToken,
    getRefreshToken,
    getSessionId,
    refreshAccessToken,
    getUserInfo,
    getUserGrade,
    isSubscriptionExpired,
    getRemainingDays,
    checkGradePermission,
    updateUserGrade,
    updateUserSubscription,
    getAllUsers,
    getLoginLogs,
    getActiveSessions,
    terminateSession,
    getSubscriptionHistory,
    getCurrentUserId,
    authAxios,
};

export default AuthService;
